/**
 * Factory for the route-guard wrappers used by App.tsx.
 *
 * Lifted out of App.tsx so the actual gating behaviour (auth check,
 * returnTo handling) can be exercised by a router-mount integration
 * test without dragging the entire shell (sidebar, theme provider,
 * query client, etc.) into the test bundle.
 */
// React must be in scope as a runtime value — the project's tsconfig
// uses jsx="preserve" and the tsx test runner emits classic React
// JSX (`React.createElement`) calls. Without this import the test
// suite throws `ReferenceError: React is not defined`.
import * as React from "react";
import type { ComponentType, ReactNode } from "react";
import { Redirect } from "wouter";
import { useAuth } from "./auth-context";

// Reference React explicitly so eslint/tree-shaking doesn't drop it.
const _ReactRef: typeof React = React;
void _ReactRef;

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

interface MakeProtectedRouteOpts {
  shell: ComponentType<{ children: ReactNode }>;
  basePath?: string;
}

export function makeProtectedRoute({
  shell: Shell,
  basePath = "",
}: MakeProtectedRouteOpts) {
  return function ProtectedRoute({
    component: Component,
  }: {
    component: ComponentType;
  }) {
    const { user, isLoading } = useAuth();
    if (isLoading) return <RouteFallback />;
    if (!user) {
      const current =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/";
      const relative =
        basePath && current.startsWith(basePath)
          ? current.slice(basePath.length)
          : current;
      if (relative && relative !== "/" && relative !== "") {
        return (
          <Redirect to={`/login?returnTo=${encodeURIComponent(relative)}`} replace />
        );
      }
      return <Redirect to="/login" replace />;
    }
    return (
      <Shell>
        <Component />
      </Shell>
    );
  };
}
